import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import TuneIcon from '@mui/icons-material/Tune';
import CheckIcon from '@mui/icons-material/Check';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { transactionApi } from '@/api/payments/transactionApi';
import { type Location } from '@/models/account-management/Location';
import {
    CARD_ISSUERS,
    TRANSACTION_TYPES,
    cardIssuerLabel,
    type TransactionFilters,
    type TransactionListResponse,
    type TransactionSummary,
} from '@/models/payments/Transaction';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { fillHeight } from '../../theme';
import TransactionDetailDialog from './TransactionDetailDialog';
import CardBrandChip from './CardBrandChip';
import { formatDateTime, formatMoney } from './format';
import { useAssignedLocations } from '../../permissions/useLocationScope';

// datetime-local input value (local wall time) -> ISO UTC for the API. Empty -> undefined.
const toIso = (localValue: string): string | undefined =>
    localValue ? new Date(localValue).toISOString() : undefined;

// Format a Date as a datetime-local input value (local wall time, no seconds/offset).
const pad = (n: number): string => String(n).padStart(2, '0');
const toLocalInput = (d: Date): string =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
// Start filter defaults to 24 hours ago.
const defaultStart = (): string => toLocalInput(new Date(Date.now() - 24 * 60 * 60 * 1000));

const PAGE_SIZE = 25;

// Approval code shown on the (collapsed) transaction row: the sale's code, plus the void's code appended
// when the transaction was voided — "primaryCode, voidCode".
const approvalDisplay = (t: TransactionSummary | { approvalCode?: string | null; isVoided: boolean; voidApprovalCode?: string | null }): string =>
    [t.approvalCode, t.isVoided ? t.voidApprovalCode : null].filter(Boolean).join(', ');

interface DraftFilters {
    locationIds: string[];
    start: string;   // datetime-local raw
    end: string;     // datetime-local raw
    cardIssuer: string;
    transactionType: string;
    invoiceNumber: string;
}

// A blank draft: no locations checked (the location default is applied by an effect once the brand's
// locations load), start defaults to the last 24 hours.
const emptyDraft = (): DraftFilters => ({
    locationIds: [], start: defaultStart(), end: '', cardIssuer: '', transactionType: '', invoiceNumber: '',
});

const Transactions = () => {
    const strings = useLocalizedStrings();
    const app = useAppContext();
    const currentLocationId = app.locationId ?? '';
    const viewedOrgId = app.organizationId ?? undefined;

    const [draft, setDraft] = useState<DraftFilters>(() => emptyDraft());
    const [applied, setApplied] = useState<TransactionFilters>({ organizationId: viewedOrgId, page: 0, pageSize: PAGE_SIZE });
    const [selectedId, setSelectedId] = useState<string | null>(null);
    // Nothing loads until the user hits Search — this is a report, not a live grid.
    const [hasSearched, setHasSearched] = useState(false);
    // Card type / transaction type / invoice filters are hidden behind a toggle by default.
    const [showAdvanced, setShowAdvanced] = useState(false);
    // True while an export is fetching all pages + building the workbook.
    const [exporting, setExporting] = useState(false);
    // The (brand|location) scope we last applied the location default for — guards the default effect so it
    // never overrides the user's manual (un)checking.
    const defaultedScope = useRef<string | null>(null);

    // Locations for the in-page Location filter (same source as the top scope picker).
    // Only the locations this user is assigned to - the location filter offered the brand's whole estate (TTD-160).
    const { locations } = useAssignedLocations(app.brandId);

    const brandLocationIds = useMemo(
        () => (locations ?? []).map((l) => l.id).filter((id): id is string => !!id),
        [locations],
    );

    // Changing the viewed ORG is a wholesale reset — a different org has entirely different locations, so the
    // date/advanced filters start fresh too; the location default re-applies via the effect below.
    useEffect(() => {
        setDraft(emptyDraft());
        setApplied({ organizationId: viewedOrgId, page: 0, pageSize: PAGE_SIZE });
        setHasSearched(false);
        defaultedScope.current = null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewedOrgId]);

    // Default the location filter: the title-bar location when one is selected, otherwise ALL of the brand's
    // locations checked. Runs once per (brand, title-bar location) so it never overrides the user's manual
    // (un)checking, and never re-clobbers the date the user was searching with.
    useEffect(() => {
        if (!currentLocationId && brandLocationIds.length === 0) return; // brand's locations not loaded yet
        const scope = `${app.brandId ?? ''}|${currentLocationId}`;
        if (defaultedScope.current === scope) return;
        defaultedScope.current = scope;
        setDraft((d) => ({ ...d, locationIds: currentLocationId ? [currentLocationId] : brandLocationIds }));
        setHasSearched(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [app.brandId, currentLocationId, brandLocationIds]);

    const { data, isFetching } = useQuery<TransactionListResponse>({
        queryKey: ['transactions', applied],
        queryFn: () => transactionApi.list(currentLocationId, applied),
        enabled: hasSearched,
        refetchOnWindowFocus: false,
        staleTime: 0,
    });

    const allLocationsChecked = brandLocationIds.length > 0 && draft.locationIds.length === brandLocationIds.length;

    // Gate Search on a brand, at least one location, and a start date; the message explains why it's disabled.
    const searchDisabledReason = !app.brandId
        ? strings.selectBrandFirst
        : draft.locationIds.length === 0
            ? strings.selectLocationsRequired
            : !draft.start ? strings.startDateRequired : '';

    const search = () => {
        if (searchDisabledReason) return;
        setHasSearched(true);
        setApplied({
            organizationId: viewedOrgId,
            // Always an explicit list (the empty case is disabled) — brand-scoped so it can't spill into other
            // brands in the org.
            locationIds: draft.locationIds,
            startDateTime: toIso(draft.start),
            endDateTime: toIso(draft.end),
            // Advanced filters only apply while their section is visible.
            cardIssuer: showAdvanced ? (draft.cardIssuer || undefined) : undefined,
            transactionType: showAdvanced ? (draft.transactionType || undefined) : undefined,
            invoiceNumber: showAdvanced ? (draft.invoiceNumber.trim() || undefined) : undefined,
            page: 0,
            pageSize: PAGE_SIZE,
        });
    };

    const clearFilters = () => {
        // Reset dates/advanced to defaults but restore the location default (title-bar location, else all).
        setDraft({ ...emptyDraft(), locationIds: currentLocationId ? [currentLocationId] : brandLocationIds });
        setHasSearched(false);
        setApplied({ organizationId: viewedOrgId, page: 0, pageSize: PAGE_SIZE });
    };

    const totalCount = data?.totalCount ?? 0;
    const canExport = hasSearched && totalCount > 0 && !isFetching;

    // Export every row matching the CURRENT applied filters (not just the visible page) to an .xlsx — same
    // columns as the grid, money as real numbers. The grid is server-paged, so we page through the whole
    // result set (200/req, the backend's max) and build the workbook client-side.
    const handleExport = async () => {
        if (!canExport || exporting) return;
        setExporting(true);
        try {
            const EXPORT_PAGE_SIZE = 200;
            const rows: TransactionSummary[] = [];
            for (let page = 0; rows.length < totalCount; page++) {
                const resp = await transactionApi.list(currentLocationId, { ...applied, page, pageSize: EXPORT_PAGE_SIZE });
                if (resp.items.length === 0) break;   // safety against an off-by-one loop
                rows.push(...resp.items);
            }

            const header = [
                strings.location, strings.date, strings.invoiceNumberShort, strings.approvalCode,
                strings.cardType, strings.voided, strings.tax, strings.tip, strings.total,
            ];
            const aoa: (string | number)[][] = [
                header,
                ...rows.map((t) => [
                    t.locationName ?? '',
                    formatDateTime(t.transactionDateTimeLocal, t.locale),
                    t.invoiceNumber ?? '',
                    approvalDisplay(t),
                    cardIssuerLabel(t.cardIssuer),
                    t.isVoided ? strings.yes : strings.no,
                    t.tax / 100,
                    t.tip / 100,
                    t.amount / 100,
                ]),
            ];

            // Dynamic import keeps SheetJS out of the main bundle (it's large and only needed on export).
            const XLSX = await import('xlsx');
            const ws = XLSX.utils.aoa_to_sheet(aoa);

            // Money columns (Tax/Tip/Total = 6/7/8) get a 2-decimal number format.
            const moneyCols = [6, 7, 8];
            const range = XLSX.utils.decode_range(ws['!ref']!);
            for (let r = 1; r <= range.e.r; r++) {
                for (const c of moneyCols) {
                    const cell = ws[XLSX.utils.encode_cell({ r, c })];
                    if (cell) cell.z = '#,##0.00';
                }
            }
            ws['!cols'] = header.map((h, i) => ({ wch: moneyCols.includes(i) ? 12 : Math.max(14, h.length + 2) }));

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, strings.transactions);
            XLSX.writeFile(wb, `transactions-${toLocalInput(new Date()).slice(0, 10)}.xlsx`);
        } finally {
            setExporting(false);
        }
    };

    const columns: GridColDef<TransactionSummary>[] = useMemo(() => [
        { field: 'locationName', headerName: strings.location, flex: 1.1, sortable: false },
        { field: 'transactionDateTimeLocal', headerName: strings.date, flex: 1.1, sortable: false, renderCell: (p) => formatDateTime(p.row.transactionDateTimeLocal, p.row.locale) },
        { field: 'invoiceNumber', headerName: strings.invoiceNumberShort, flex: 0.9, sortable: false },
        { field: 'approvalCode', headerName: strings.approvalCode, flex: 0.9, sortable: false, renderCell: (p) => approvalDisplay(p.row) },
        { field: 'cardIssuer', headerName: strings.cardType, flex: 0.9, sortable: false, align: 'center', headerAlign: 'center', renderCell: (p) => <CardBrandChip issuer={p.value as string} /> },
        {
            field: 'isVoided', headerName: strings.voided, flex: 0.6, sortable: false, filterable: false, align: 'center', headerAlign: 'center',
            renderCell: (p) => (p.row.isVoided ? <CheckIcon fontSize="small" color="action" /> : null),
        },
        { field: 'tax', headerName: strings.tax, flex: 0.6, sortable: false, align: 'right', headerAlign: 'right', renderCell: (p) => formatMoney(p.row.tax, p.row.locale) },
        { field: 'tip', headerName: strings.tip, flex: 0.6, sortable: false, align: 'right', headerAlign: 'right', renderCell: (p) => formatMoney(p.row.tip, p.row.locale) },
        { field: 'amount', headerName: strings.total, flex: 0.7, sortable: false, align: 'right', headerAlign: 'right', renderCell: (p) => formatMoney(p.row.amount, p.row.locale) },
        {
            field: 'actions', headerName: strings.actions, flex: 0.9, sortable: false, filterable: false, align: 'center', headerAlign: 'center',
            renderCell: (p) => (
                <Button
                    size="small"
                    variant="contained"
                    onClick={() => setSelectedId(p.row.id)}
                    sx={{ fontSize: '0.78rem', py: 0.4, px: 1.5, minWidth: 0 }}
                >
                    {strings.viewDetails}
                </Button>
            ),
        },
    ], [strings]);

    return (
        <Box sx={{ ...fillHeight, overflow: 'auto', p: 3 }}>
            {/* Filter bar — visually separated from the grid with its own background + bottom border.
                Negative margins pull it out to the page edges against the container's p:3. */}
            <Box sx={{ mx: -3, mt: -3, mb: 3, px: 3, pt: 2, pb: 0 }}>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <TextField
                    select label={strings.location} value={draft.locationIds} sx={{ minWidth: 220, maxWidth: 320 }} size="small"
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{
                        multiple: true,
                        displayEmpty: true,
                        renderValue: (selected) => {
                            const ids = selected as string[];
                            if (ids.length === 0) return <em>{strings.selectLocationsLabel}</em>;
                            if (allLocationsChecked) return strings.allLocations;
                            if (ids.length === 1) return (locations ?? []).find((l) => l.id === ids[0])?.name ?? ids[0];
                            return strings.multipleLocationsSelected;
                        },
                    }}
                    onChange={(e) => setDraft({ ...draft, locationIds: e.target.value as unknown as string[] })}
                >
                    {(locations ?? []).map((l) => (
                        <MenuItem key={l.id} value={l.id}>
                            <Checkbox size="small" sx={{ py: 0, pl: 0 }} checked={draft.locationIds.includes(l.id ?? '')} />
                            <ListItemText primary={l.name} />
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    required label={strings.startDateTime} type="datetime-local" size="small" sx={{ minWidth: 210 }}
                    InputLabelProps={{ shrink: true }} value={draft.start} error={!draft.start}
                    onChange={(e) => setDraft({ ...draft, start: e.target.value })}
                />
                <TextField
                    label={strings.endDateTime} type="datetime-local" size="small" sx={{ minWidth: 210 }}
                    InputLabelProps={{ shrink: true }} value={draft.end}
                    onChange={(e) => setDraft({ ...draft, end: e.target.value })}
                />
                {showAdvanced && (
                    <>
                        <TextField
                            select label={strings.cardType} value={draft.cardIssuer} sx={{ minWidth: 170 }} size="small"
                            onChange={(e) => setDraft({ ...draft, cardIssuer: e.target.value })}
                        >
                            <MenuItem value=""><em>{strings.all}</em></MenuItem>
                            {CARD_ISSUERS.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                        </TextField>
                        <TextField
                            select label={strings.transactionType} value={draft.transactionType} sx={{ minWidth: 150 }} size="small"
                            onChange={(e) => setDraft({ ...draft, transactionType: e.target.value })}
                        >
                            <MenuItem value=""><em>{strings.all}</em></MenuItem>
                            {TRANSACTION_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                        </TextField>
                        <TextField
                            label={strings.invoiceNumber} size="small" sx={{ minWidth: 170 }} value={draft.invoiceNumber}
                            onChange={(e) => setDraft({ ...draft, invoiceNumber: e.target.value })}
                            onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
                        />
                    </>
                )}
                <Tooltip title={showAdvanced ? strings.fewerFilters : strings.moreFilters}>
                    <Button variant="outlined" startIcon={<TuneIcon />} onClick={() => setShowAdvanced((v) => !v)}>
                        {showAdvanced ? strings.fewerFilters : strings.moreFilters}
                    </Button>
                </Tooltip>
                {/* Search is disabled until a brand is picked and a start date is set — a span wrapper lets the
                    tooltip show over the disabled button. */}
                <Tooltip title={searchDisabledReason}>
                    <span>
                        <Button variant="contained" onClick={search} disabled={!!searchDisabledReason}>{strings.search}</Button>
                    </span>
                </Tooltip>
                <Button variant="text" onClick={clearFilters}>{strings.clear}</Button>
                <Button
                    variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExport}
                    disabled={!canExport} loading={exporting}
                    sx={{ ml: 'auto' }}
                >
                    {strings.export}
                </Button>
            </Stack>
            </Box>

            <DataGrid
                rows={hasSearched ? (data?.items ?? []) : []}
                columns={columns}
                getRowId={(r) => r.id}
                loading={isFetching}
                paginationMode="server"
                rowCount={hasSearched ? (data?.totalCount ?? 0) : 0}
                paginationModel={{ page: applied.page, pageSize: applied.pageSize }}
                onPaginationModelChange={(m) => setApplied((prev) => ({ ...prev, page: m.page, pageSize: m.pageSize }))}
                pageSizeOptions={[25, 50, 100]}
                disableRowSelectionOnClick
                localeText={{ noRowsLabel: hasSearched ? strings.noTransactions : strings.searchToLoad }}
            />

            <TransactionDetailDialog
                open={selectedId !== null}
                transactionId={selectedId}
                locationId={currentLocationId}
                organizationId={viewedOrgId}
                onClose={() => setSelectedId(null)}
            />
        </Box>
    );
};

export default Transactions;
